<template>
  <div class="nested-view">
    <template v-if="points.length === 0">
      <section class="section">
        <div class="empty">
          <i class="material-icons">group_work</i>
          {{ $t("points.empty") }}
        </div>
      </section>
    </template>
    <template v-else-if="points.length === 1">
      <point-item :point="points[0]" />
    </template>
    <template v-else>
      <div class="section-title">{{ $t("points.programs") }}</div>
      <section class="points">
        <div
          class="point-item"
          :class="{ 'has-image': point.image }"
          :style="pointItemStyle(point, index)"
          v-for="(point, index) in points"
          v-bind:key="'point#' + point.token"
          v-on:click="selectPointItem(point)"
        >
          <template v-if="point.image">
            <div class="point-item-background" :style="'background-image: url(' + point.image + ')'"></div>
            <div class="point-item-header">
              <div class="point-item-left">
                <div class="name">{{ point.title }}</div>
              </div>
              <div class="point-item-right">
                <div class="amount">{{ point.point | properNumber }}</div>
                <div class="unit">
                  {{ point.type === 'DEPOSIT' ? $tc("points.dollars", point.point) : $tc("points.points", point.point) }}
                </div>
              </div>
            </div>
            <div class="point-item-desc">{{ point.content }}</div>
          </template>
          <template v-else>
            <div class="point-item-header">
              <template v-if="point.type === 'MILESTONE'">
                <div class="point-item-left">
                  <div class="hex">
                    <svg width="44" height="44" xmlns="http://www.w3.org/2000/svg"><path d="M26 1.31l11.919 6.88a8 8 0 014 6.929V28.88a8 8 0 01-4 6.928L26 42.691a8 8 0 01-8 0L6.081 35.809a8 8 0 01-4-6.928V15.12a8 8 0 014-6.928L18 1.309a8 8 0 018 0z" :fill="`#${point.color}`" fill-rule="evenodd"/></svg>
                    <div class="icon" :style="'background-image: url(' + point.icon + ')'"></div>
                  </div>
                </div>
                <div class="point-item-right">
                  <div class="amount">{{ Math.round(point.point / point.goal * 100) }}</div>
                  <div class="unit">%</div>
                </div>
              </template>
              <template v-else>
                <div class="point-item-left">
                  <div class="icon" :style="'background-image: url(' + point.icon + ')'"></div>
                </div>
                <div class="point-item-right">
                  <div class="amount">{{ point.point | properNumber }}</div>
                  <div class="unit">
                    {{ point.type === 'DEPOSIT' ? $tc("points.dollars", point.point) : $tc("points.points", point.point) }}
                  </div>
                </div>
              </template>
            </div>
            <div class="point-item-title">
              <div class="name">{{ point.title }}</div>
            </div>
            <div class="point-item-desc">{{ point.content }}</div>
            <div
              class="point-item-progress"
              v-if="point.type === 'MILESTONE'"
            >
              <div class="base" :style="'background-color: #' + point.color " />
              <div
                class="fill"
                :style="'width: ' + Math.round(point.point / point.goal * 100) + '%; background-color: #' + point.color "
              ></div>
            </div>
          </template>
        </div>
      </section>
    </template>
    <powered-by></powered-by>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import PointItem from '@/components/PointItem.vue';
import PoweredBy from '@/components/PoweredBy.vue';

export default {
  name: 'Points',
  computed: {
    ...mapState(['client', 'brand', 'points', 'point']),
  },
  methods: {
    ...mapMutations(['setLoader']),
    ...mapActions(['getPoint']),
    pointItemStyle(pointItem) {
      if (pointItem.image) {
        return {
          'box-shadow': '0 1px 3px 0 rgba(0, 0, 0, .15)',
          'background-color': 'transparent',
          'background-image': 'linear-gradient(-180deg, rgba(51, 51, 51, .15) 0%, rgba(51, 51, 51, .75) 100%)',
          color: 'white',
        };
      }

      return {
        'background-color': pointItem.bg_color ? `#${pointItem.bg_color}` : null,
        color: pointItem.color ? `#${pointItem.color}` : null,
      };
    },
    selectPointItem(pointItem) {
      this.setLoader(true);
      this.$router.push({ name: 'Point', params: { token: pointItem.token } });
    },
  },
  watch: {
    points(value) {
      if (value.length === 1) {
        this.getPoint(value[0].token);
      }
    },
  },
  mounted() {
    if (this.points.length === 1) {
      this.getPoint(this.points[0].token);
    }
  },
  components: {
    PointItem,
    PoweredBy,
  },
};
</script>

<style lang="sass" scoped>
.section-title
  margin: 24px 24px 8px

  color: #686868
  font-size: 0.875rem
  font-weight: bold
  letter-spacing: 2px
  text-transform: uppercase

  & + .section
    margin-top: 0

  &-container
    display: flex
    align-items: center
    margin: 24px 24px 8px

    & + .section
      margin-top: 0

    .section-title
      flex-grow: 1
      margin: 0

.empty
  padding: 15% 0

  text-align: center
  font-weight: bold
  color: #CFD8DC

  .material-icons
    display: block
    margin-bottom: 12px

    font-size: 5.5rem

.points
  .point-item
    position: relative
    margin: 16px
    padding: 16px
    overflow: hidden
    background-color: #F5F5F5

    border-radius: 16px

    color: #9E9E9E

    cursor: pointer

    &:first-child
      margin-top: 0

    &:active
      opacity: .5

      transform: scale(.95)

    &.has-image
      .point-item-desc
        -webkit-line-clamp: 5

    &-background
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      z-index: -1

      background-size: cover
      background-position: center

    &-header
      display: flex
      align-items: center
      margin-bottom: 16px

    &-left
      flex-grow: 1

    &-right
      text-align: right
      white-space: nowrap

    .name
      flex-grow: 1
      margin-right: 24px

      font-weight: bold

    .amount,
    .unit
      display: inline-block

    .amount
      font-weight: 300
      font-size: 2.25rem

    .unit
      margin-left: 4px

      font-size: 0.75rem
      font-weight: bold

    .hex
      width: 44px
      height: 44px
      position: relative
      overflow: hidden

      .icon
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0

        background-color: transparent

    .icon
      width: 44px
      height: 44px

      background-color: currentcolor
      background-size: cover
      background-position: center

      border-radius: 100%

    &-title
      margin-bottom: 8px

    &-desc
      overflow: hidden
      display: -webkit-box
      -webkit-box-orient: vertical
      -webkit-line-clamp: 2

      font-size: 0.875rem

    &-progress
      position: relative
      margin-top: 16px
      height: 4px
      overflow: hidden

      border-radius: 4px

      .base,
      .fill
        height: 4px

        border-radius: 4px

      .base
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0
        z-index: 1
        opacity: .1

      .fill
        position: absolute
        top: 0
        right: auto
        bottom: 0
        left: 0
        z-index: 2

        animation-duration: 1.5s
        animation-name: bar
        animation-timing-function: ease-in-out

@keyframes bar
  from
    width: 0%
</style>
